import { Person } from './person';

export interface PerformedExamAuds {
  revision: {
    id: number;
    timestamp: string;
    person: Person;
  };
  type: 'INSERT' | 'UPDATE' | 'DELETE';
  entity: PerformedExam;
  previousEntity?: PerformedExam;
}

export interface Exams {
  id: number;
  createdAt?: string;
  name?: string;
  synonyms?: string;
  updatedAt?: string;
  fileUrlRequired?: boolean;
  examGroup?: string;
  expectedExamResults?: ExpectedExamResults[];
}

export interface PerformedExam {
  id?: number;
  performedAt?: string;
  documentUrl?: string;
  requestedBy?: 'MAR' | 'OTHERS';
  laboratory?: any;
  person?: Person;
  exam?: Exams;
  examResults?: ExamResult[];
  report?: string;
  examParseId?: number;
  examParse?: ExamParses;
  source?: 'MANUAL' | 'LAB_INTEGRATION';
}

export interface ExamResult {
  value: string;
  expectedExamResult: ExpectedExamResults;
  performedExam?: PerformedExam;
}

export interface ExpectedExamResults {
  createdAt?: string;
  id?: number;
  name?: string;
  unit?: string;
  updatedAt?: string;
  valueType?: 'TEXT' | 'NUMBER' | 'CHOICE' | 'CHOICES' | 'NUMERIC';
  options: string[];
  exam?: Exams;
}

export interface ExamRequestApiModel {
  id: number;
  createdAt?: string;
  updatedAt?: string;
  requestedAt?: string;
  patientCanRegenerate?: boolean;
  performed?: boolean;
  notes?: string;
  observation?: string;
  person?: Person;
  exam?: Exams;
  exams?: Exams[];
  lastMedicalDocument?: {
    documentUrl?: string;
  };
}

export type ExamRequestStatus = 'PENDING' | 'COMPLETED';

export interface ExamRequestPersonSummary {
  personId?: number;
  person?: Person;
}

export interface ExamRequestExamSummary {
  examId: number;
  examName?: string;
  examGroup?: string;
  fileUrlRequired?: boolean;
  isPrimary: boolean;
  exam?: Exams;
}

export interface ExamRequestDocumentLookup {
  personId?: number;
  examGroup?: string;
}

export interface ExamRequestDocumentSummary {
  documentUrl?: string;
  canRegenerate: boolean;
  lookup: ExamRequestDocumentLookup;
}

export interface ExamRequest {
  id: number;
  requestId: number;
  person: ExamRequestPersonSummary;
  primaryExamId?: number;
  primaryExam?: ExamRequestExamSummary;
  examIds: number[];
  exams: ExamRequestExamSummary[];
  notes?: string;
  status: ExamRequestStatus;
  requestedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  document: ExamRequestDocumentSummary;
}

export interface ExamRequestFormModel {
  requestId?: number;
  personId?: number;
  selectedExamIds: number[];
  primaryExamId?: number;
  notes: string;
  requestedAt?: string;
}

export interface ExamRequestSavePayload {
  id?: number;
  person: {
    id: number;
  };
  exam?: {
    id: number;
  };
  exams: Array<{
    id: number;
  }>;
  notes?: string;
  requestedAt?: string;
}

function buildExamRequestExamSummary(exam: Exams, primaryExamId?: number): ExamRequestExamSummary {
  return {
    examId: exam.id,
    examName: exam.name,
    examGroup: exam.examGroup,
    fileUrlRequired: exam.fileUrlRequired,
    isPrimary: exam.id === primaryExamId,
    exam,
  };
}

export function toExamRequest(apiModel: ExamRequestApiModel): ExamRequest {
  const rawExams = [apiModel.exam, ...(apiModel.exams ?? [])]
    .filter((exam): exam is Exams => !!exam?.id)
    .filter((exam, index, exams) => exams.findIndex(({ id }) => id === exam.id) === index);

  const primaryRawExam = apiModel.exam ?? rawExams[0];
  const exams = rawExams.map((exam) => buildExamRequestExamSummary(exam, primaryRawExam?.id));

  return {
    id: apiModel.id,
    requestId: apiModel.id,
    person: {
      personId: apiModel.person?.id,
      person: apiModel.person,
    },
    primaryExamId: primaryRawExam?.id,
    primaryExam: primaryRawExam ? buildExamRequestExamSummary(primaryRawExam, primaryRawExam.id) : undefined,
    examIds: exams.map(({ examId }) => examId),
    exams,
    notes: apiModel.notes ?? apiModel.observation,
    status: apiModel.performed ? 'COMPLETED' : 'PENDING',
    requestedAt: apiModel.requestedAt,
    createdAt: apiModel.createdAt,
    updatedAt: apiModel.updatedAt,
    document: {
      documentUrl: apiModel.lastMedicalDocument?.documentUrl,
      canRegenerate: !!apiModel.patientCanRegenerate,
      lookup: {
        personId: apiModel.person?.id,
        examGroup: primaryRawExam?.examGroup,
      },
    },
  };
}

export function toExamRequestFormModel(examRequest?: ExamRequest): ExamRequestFormModel {
  return {
    requestId: examRequest?.requestId,
    personId: examRequest?.person?.personId,
    selectedExamIds: examRequest?.examIds ?? [],
    primaryExamId: examRequest?.primaryExamId,
    notes: examRequest?.notes ?? '',
    requestedAt: examRequest?.requestedAt,
  };
}

export function buildExamRequestSavePayload(formModel: ExamRequestFormModel): ExamRequestSavePayload {
  const uniqueExamIds = Array.from(new Set(formModel.selectedExamIds.filter((examId) => !!examId)));

  if (!formModel.personId) {
    throw new Error('ExamRequestSavePayload requires personId.');
  }

  if (uniqueExamIds.length === 0) {
    throw new Error('ExamRequestSavePayload requires at least one examId.');
  }

  const primaryExamId = formModel.primaryExamId ?? uniqueExamIds[0];

  return {
    id: formModel.requestId,
    person: {
      id: formModel.personId,
    },
    exam: primaryExamId ? { id: primaryExamId } : undefined,
    exams: uniqueExamIds.map((id) => ({ id })),
    notes: formModel.notes?.trim() || undefined,
    requestedAt: formModel.requestedAt,
  };
}

export interface ExamParses {
  id?: number;
  createdAt?: string;
  updatedAt?: string;
  fileUrl?: string;
  fileMimeType?: string;
  assistantResult?: PerformedExam[];
  status?: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'LOADING';
  assistantStatus?: string;
  fileHash?: string;
  fileLength?: string;
  filePrettyName?: string;
}


export interface PerformedExamAudFormatted {
  examName: string;
  revisionPersonName?: string;
  revisionTimestamp: string;
  type: string;

  old?: PerformedExamAudEntityFormatted;
  current?: PerformedExamAudEntityFormatted;
}

interface PerformedExamAudEntityFormatted {
  report: string;
  performedAt?: string;
  documentUrl?: string;
  requestedBy?: string;
  results?: string[];
}
